/**
 * API Client Configuration
 * 
 * This version uses cookies for token storage for better security.
 * Authentication tokens are managed through the cookieUtils helper functions
 * which store tokens in HTTP-only cookies when possible.
 */

import { useCallback, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery, UseQueryOptions, UseMutationOptions, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { getAuthToken, removeAuthToken, removeAuthUser, setAuthToken, setAuthUser } from '../utils/cookieUtils';

// Configure Axios
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8080';


// Ensure we're using the correct protocol and port
const getApiUrl = () => {
  return API_BASE_URL;
};

// Helper function to validate and clean auth tokens
const validateAndCleanToken = (token: string | null): string | null => {
  if (!token) return null;
  
  const cleanToken = token.trim();
  
  // Check if token is empty after trimming
  if (!cleanToken) return null;
  
  // Check for valid JWT format (basic check)
  if (cleanToken.includes('.')) {
    const parts = cleanToken.split('.');
    if (parts.length === 3) {
      // Looks like a JWT, validate each part contains valid base64url characters
      // JWT uses base64url encoding which includes A-Z, a-z, 0-9, -, and _
      const base64UrlRegex = /^[A-Za-z0-9_-]*={0,2}$/;
      if (parts.every(part => base64UrlRegex.test(part))) {
        return cleanToken;
      }
    }
  }
  
  // For non-JWT tokens, ensure they only contain ASCII characters
  if (/^[\x20-\x7E]*$/.test(cleanToken)) {
    return cleanToken;
  }
  
  // If token contains invalid characters, return null
  console.warn('Invalid token format detected, removing from storage');
  return null;
};

// Bearer token is sent by the request interceptor below. withCredentials=false works everywhere
// (local, server, backend in another container/pod). Set VITE_API_CREDENTIALS=true only if frontend
// and API are same origin and you want to use HttpOnly cookie.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: false,
});

// Request interceptor to add authentication token and content type
apiClient.interceptors.request.use(
  (config) => {
    const rawToken = getAuthToken();
    const validToken = validateAndCleanToken(rawToken);
    
    if (validToken) {
      config.headers.Authorization = `Bearer ${validToken}`;
      if (import.meta.env.DEV) console.log('Added auth token to request:', config.url);
    } else if (rawToken) {
      // Token exists but is invalid, remove it
      console.warn('Invalid token detected, removing from storage. Raw token:', rawToken?.substring(0, 20) + '...');
      removeAuthToken();
    } else if (import.meta.env.DEV) {
      console.log('No token in JS (cookie auth or unauthenticated):', config.url);
    }
    
    return config;
  },
  (error:any) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token management
apiClient.interceptors.response.use(
  (response:any) => response,
  (error: AxiosError) => {
    console.error('API Error:', error);
    
    let errorMessage = 'خطا در درخواست';
    
    if (error.code === 'ECONNABORTED') {
      // Timeout: often happens with large file uploads; server may still complete the request
      errorMessage = 'درخواست به دلیل طولانی بودن قطع شد. در صورت آپلود فایل حجیم، پست ممکن است بعداً ذخیره شود. لطفاً صفحه اصلی را بررسی کنید.';
    } else if (error.code === 'NETWORK_ERROR') {
      errorMessage = 'خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید.';
    } else if (error.code === 'ECONNREFUSED') {
      errorMessage = 'خطا در دسترسی به سرور. لطفاً دوباره تلاش کنید.';
    } else if (error.code === 'ETIMEDOUT') {
      errorMessage = 'درخواست شما به دلیل کندی اتصال لغو شد. لطفاً دوباره تلاش کنید.';
    } else if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401: {
          // Session check (GET /user/profile) is expected to return 401 when not logged in — do not redirect
          const isSessionCheck = error.config?.url?.includes('/user/profile') && error.config?.method?.toLowerCase() === 'get';
          if (isSessionCheck) {
            if (typeof window !== 'undefined') {
              removeAuthToken();
              removeAuthUser();
            }
            return Promise.reject(error);
          }
          // Token expired or invalid on other requests - clear auth and redirect to login
          console.warn('401 Unauthorized: Token expired or invalid. Logging out.');
          if (typeof window !== 'undefined') {
            removeAuthToken();
            removeAuthUser();
            const path = window.location.pathname;
            if (!path.includes('/login') && !path.includes('/signup') && !path.includes('/forgot-password') && !path.includes('/auth')) {
              window.location.href = '/login';
              return Promise.reject(new Error('session_expired'));
            }
          }
          errorMessage = 'شما مجاز به انجام این عملیات نیستید. لطفاً وارد حساب کاربری خود شوید.';
          break;
        }
        case 403:
          errorMessage = 'دسترسی شما محدود شده است.';
          break;
        case 404:
          errorMessage = 'مورد درخواستی یافت نشد.';
          break;
        case 409:
          // Conflict - use the message from the server (e.g., phone number already exists)
          errorMessage = (data as any)?.message || (data as any)?.error || 'این عملیات با اطلاعات موجود در تضاد است.';
          break;
        case 429:
          errorMessage = 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً کمی صبر کنید.';
          break;
        case 500:
        case 502:
        case 503:
          errorMessage = 'خطا در سرور. لطفاً بعداً دوباره تلاش کنید.';
          break;
        case 504:
          // Gateway timeout: proxy timed out waiting for backend; upload may still complete
          errorMessage = 'درخواست به دلیل طولانی بودن قطع شد. در صورت آپلود فایل حجیم، پست ممکن است بعداً ذخیره شود. لطفاً صفحه اصلی را بررسی کنید.';
          break;
        default:
          errorMessage = (data as any)?.message || (data as any)?.error || `خطای ${status}`;
      }
    }
    
    return Promise.reject(new Error(errorMessage));
  }
);

// API Response types
export interface Post {
  id: string;
  userId: string;
  image: string;
  video?: string | null;
  caption: string;
  postType?: 'regular' | 'poll';
  createdAt: string;
  user?: {
    id: number;
    username: string;
    name: string;
    image: string;
    verified?: boolean;
  };
  comments?: Comment[];
  likes?: Like[];
  poll?: {
    id: number;
    question: string;
    options: Array<{
      id: number;
      optionText: string;
      voteCount: number;
      percentage: number;
    }>;
    totalVotes: number;
    userVote?: number | null;
  };
}

export interface Comment {
  id: string;
  postId: number;
  userId: string;
  content: string;
  createdAt: string;
  user?: {
    id: number;
    username: string;
    name: string;
    image: string;
    verified?: boolean;
  };
}

export interface Like {
  id: string;
  post_id: string;
  user_id: string;
  username: string;
  user_image: string;
  createdAt: string;
}

export interface Story {
  id: string;
  userId: number;
  content: string;
  media: string;
  /** When set, this story is a "post share" — tap to open the related post. */
  postId?: number | null;
  createdAt: string;
  /** True if the current user has viewed this story (persisted on backend). */
  seen?: boolean;
  user?: {
    id: number;
    username: string;
    name: string;
    image: string;
    description?: string;
    verified?: boolean;
  };
}

export interface Tag {
  id: number;
  name: string;
  createdAt: string;
  count: number;
}

// Direct message types
export interface DMUser {
  id: number;
  name: string;
  username: string;
  image?: string;
  verified?: boolean;
  /** Real-time online status from WebSocket presence (set by GetConversation + user_presence) */
  online?: boolean;
}

export interface DMConversation {
  id: number;
  user1Id: number;
  user2Id: number;
  lastMessageAt?: string | null;
  lastMessagePreview?: string;
  lastMessageId?: number | null;
  createdAt: string;
  updatedAt: string;
  user1?: DMUser;
  user2?: DMUser;
}

export interface DMMessage {
  id: number;
  conversationId: number;
  senderId: number;
  body: string;
  attachmentUrl?: string;
  /** When set, this message was sent as a reply to a story (DM from story viewer). */
  storyId?: number | null;
  readAt?: string | null;
  createdAt: string;
  sender?: DMUser;
}

// Query Keys
export const queryKeys = {
  posts: ['posts'] as const,
  post: (id: string) => ['posts', id] as const,
  postsByTag: (tag: string) => ['posts', 'tag', tag] as const,
  postsByTags: (tags: string[]) => ['posts', 'tags', ...tags.slice().sort()] as const,
  postsByUser: (userId: string) => ['posts', 'user', userId] as const,
  comments: (postId: string) => ['comments', postId] as const,
  likes: (postId: string) => ['likes', postId] as const,
  stories: ['stories'] as const,
  tags: ['tags'] as const,
  tagsSearch: (q: string) => ['tags', 'search', q] as const,
  popularTags: ['tags', 'popular'] as const,
  tagsByPost: (postId: string) => ['tags', 'post', postId] as const,
  user: (userId: string) => ['users', userId] as const,
  userPostCount: (userId: string) => ['users', userId, 'posts', 'count'] as const,
  coinHistory: ['user', 'coins', 'history'] as const,
  allUsers: ['users', 'all'] as const,
  coinLeaderboard: ['users', 'coin-leaderboard'] as const,
  verificationRequest: ['verification-request'] as const,
  notifications: ['notifications'] as const,
  unreadNotificationCount: ['notifications', 'unread-count'] as const,
  // Direct messages
  dmConversations: ['dm', 'conversations'] as const,
  dmConversation: (id: string) => ['dm', 'conversations', id] as const,
  dmMessages: (conversationId: string) => ['dm', 'messages', conversationId] as const,
  dmUnreadCount: ['dm', 'unread-count'] as const,
  // Prediction games
  games: ['games'] as const,
  game: (id: string) => ['games', id] as const,
  gameConstants: ['games', 'constants'] as const,
};

// Posts API Functions
const fetchPosts = async (page: number = 1, limit: number = 3) => {
  const response = await apiClient.get(`/posts?page=${page}&limit=${limit}&include_details=true`);
  return response.data;
};

/** Fetch only video posts (reels) with pagination */
const fetchReels = async (page: number = 1, limit: number = 10) => {
  const response = await apiClient.get(`/posts?page=${page}&limit=${limit}&video_only=true&include_details=true`);
  return response.data;
};

const fetchPost = async (id: string, isHashBased: boolean = false) => {
  const endpoint = isHashBased ? `/p/${id}?include_details=true` : `/posts/${id}?include_details=true`;
  const response = await apiClient.get(endpoint);
  return response.data;
};

const fetchPostsByTag = async (tagName: string, page: number = 1, limit: number = 3) => {
  const response = await apiClient.get(`/posts/tag/${encodeURIComponent(tagName)}?page=${page}&limit=${limit}`);
  return response.data;
};

const fetchPostsByUser = async (userId: string, page: number = 1, limit: number = 12, postType?: 'regular' | 'poll' | 'text') => {
  const typeParam = postType ? `&type=${postType}` : '';
  const response = await apiClient.get(`/posts/user/${userId}?page=${page}&limit=${limit}${typeParam}`);
  return response.data;
};

const createPost = async (postData: {
  caption: string;
  imageFile?: File;
  videoFile?: File;
  coverFile?: File;
  files?: File[];
  tags?: string[];
  postType?: 'regular' | 'poll' | 'text';
  pollData?: {question: string; options: string[]};
  onUploadProgress?: (progressEvent: any) => void;
}) => {
  const form = new FormData();
  form.append('caption', postData.caption);
  form.append('postType', postData.postType || 'text');
  
  // Add poll data if provided
  if (postData.postType === 'poll' && postData.pollData) {
    form.append('pollQuestion', postData.pollData.question);
    form.append('pollOptions', JSON.stringify(postData.pollData.options));
  }
  
  // Add tags if provided
  if (postData.tags && postData.tags.length > 0) {
    // Send tags as comma-separated string
    form.append('tags', postData.tags.join(','));
  }
  
  // Only upload files if post type is 'regular'
  if (postData.postType === 'regular') {
    // For video posts, send both video and cover image
    if (postData.videoFile) {
      form.append('video', postData.videoFile);
      if (postData.coverFile) {
        form.append('image', postData.coverFile); // Cover image as the main image
      }
    } else if (postData.imageFile) {
      // For image-only posts
      form.append('image', postData.imageFile);
    }

    // Handle multiple files
    if (postData.files && postData.files.length > 0) {
      postData.files.forEach((file) => {
        form.append('files', file);
      });
    }
  }

  // Use long timeout for large video/file uploads (10 min); default apiClient timeout is 30s
  // Do not set Content-Type so axios sets multipart/form-data with correct boundary (required for server to parse postType, pollQuestion, pollOptions)
  const response = await apiClient.post('/posts', form, {
    onUploadProgress: postData.onUploadProgress,
    timeout: 600000, // 10 minutes for large files
  });
  return response.data;
};

// Poll API functions
const voteOnPostPoll = async (pollId: number, optionId: number) => {
  const response = await apiClient.post(`/post-polls/${pollId}/vote`, { optionId });
  return response.data;
};

const getPostPollResults = async (pollId: number) => {
  const response = await apiClient.get(`/post-polls/${pollId}/results`);
  return response.data;
};

// Hash-based poll API functions
const voteOnPostPollByHash = async (hashCode: string, optionId: number) => {
  const response = await apiClient.post(`/p/${hashCode}/poll/vote`, { optionId });
  return response.data;
};

const getPostPollResultsByHash = async (hashCode: string) => {
  const response = await apiClient.get(`/p/${hashCode}/poll/results`);
  return response.data;
};

// New function for uploading multiple files separately
const uploadMultipleFiles = async (files: File[], onUploadProgress?: (progressEvent: any) => void) => {
  const form = new FormData();
  files.forEach((file) => {
    form.append('files', file);
  });

  const response = await apiClient.post('/upload/multiple', form, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
    timeout: 300000, // 5 minutes timeout
  });
  return response.data;
};

// Single file upload function
const uploadSingleFile = async (file: File, onUploadProgress?: (progressEvent: any) => void) => {
  const form = new FormData();
  form.append('file', file);

  const response = await apiClient.post('/upload', form, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress,
    timeout: 300000, // 5 minutes timeout
  });
  return response.data;
};

const deletePost = async (postId: string) => {
  const response = await apiClient.delete(`/posts/${postId}`);
  return response.data;
};

// Posts Hooks
export const usePosts = (
  page: number = 1, 
  limit: number = 3, 
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.posts, page, limit],
    queryFn: () => fetchPosts(page, limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const usePost = (
  id: string, 
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'> & { isHashBased?: boolean }
) => {
  const { isHashBased = false, ...queryOptions } = options || {};
  
  return useQuery({
    queryKey: [...queryKeys.post(id), isHashBased ? 'hash' : 'id'],
    queryFn: () => fetchPost(id, isHashBased),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...queryOptions,
  });
};

export const usePostsByTag = (
  tagName: string, 
  page: number = 1, 
  limit: number = 3,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.postsByTag(tagName), page, limit],
    queryFn: () => fetchPostsByTag(tagName, page, limit),
    enabled: !!tagName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const usePostsByUser = (
  userId: string, 
  page: number = 1, 
  limit: number = 12,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.postsByUser(userId), page, limit],
    queryFn: () => fetchPostsByUser(userId, page, limit),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useInfinitePostsByUser = (
  userId: string,
  limit: number = 8,
  postType?: 'regular' | 'poll' | 'text',
  options?: Omit<UseInfiniteQueryOptions<any, Error, any, any, number>, 'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'>
) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.postsByUser(userId), 'infinite', limit, postType || 'all'],
    queryFn: ({ pageParam }: { pageParam: number }) => fetchPostsByUser(userId, pageParam, limit, postType),
    enabled: !!userId,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer posts than the limit, there are no more pages
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < limit) {
        return undefined;
      }
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCreatePost = (
  options?: UseMutationOptions<any, Error, Parameters<typeof createPost>[0]>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createPost,
    onSuccess: (data, variables, context) => {
      // Invalidate posts queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      // Immediately refetch to show new content
      queryClient.refetchQueries({ queryKey: queryKeys.posts });
      
      // Invalidate all user post counts since we don't know which user created the post
      // This is a bit broad but ensures consistency
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      // Notify service worker to refresh cache if app is installed as PWA
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_UPDATE',
          action: 'invalidate',
        });
      }
    },
    ...options,
  });
};

// Hook for uploading multiple files
export const useUploadMultipleFiles = (
  options?: UseMutationOptions<any, Error, { files: File[]; onUploadProgress?: (progressEvent: any) => void }>
) => {
  return useMutation({
    mutationFn: ({ files, onUploadProgress }) => uploadMultipleFiles(files, onUploadProgress),
    ...options,
  });
};

// Hook for uploading single file
export const useUploadSingleFile = (
  options?: UseMutationOptions<any, Error, { file: File; onUploadProgress?: (progressEvent: any) => void }>
) => {
  return useMutation({
    mutationFn: ({ file, onUploadProgress }) => uploadSingleFile(file, onUploadProgress),
    ...options,
  });
};

export const useDeletePost = (
  options?: UseMutationOptions<any, Error, string>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deletePost,
    onSuccess: () => {
      // Invalidate all posts queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      
      // Invalidate all user post counts since we don't know which user deleted the post
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => {
      // Refetch on error to restore correct state
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
    },
    ...options,
  });
};

// Comments API Functions
const fetchComments = async (postId: string) => {
  const response = await apiClient.get(`/comments?post_id=${postId}`);
  return response.data;
};

const createComment = async (commentData: {
  post_id: string;
  user_id: number;
  content: string;
}) => {
  const bodyData = {
    postId: Number(commentData.post_id),
    user_id: commentData.user_id,
    content: commentData.content,
  };
  const response = await apiClient.post('/comments', bodyData);
  return response.data;
};

const deleteComment = async (commentId: string) => {
  const response = await apiClient.delete(`/comments/${commentId}`);
  return response.data;
};

// Comments Hooks
export const useComments = (
  postId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.comments(postId),
    queryFn: () => fetchComments(postId),
    enabled: !!postId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useCreateComment = (
  options?: UseMutationOptions<any, Error, Parameters<typeof createComment>[0], any>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      // Invalidate queries to get the real data from server (feed + reels)
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      queryClient.invalidateQueries({ queryKey: ['reels-posts'] });
    },
    onError: (error, variables, context) => {
      // Remove optimistic comment on error
      if (context?.optimisticComment) {
        queryClient.setQueriesData(
          { queryKey: queryKeys.posts },
          (oldData: any) => {
            if (!oldData) return oldData;
            
            // Handle infinite query structure
            if (oldData.pages) {
              return {
                ...oldData,
                pages: oldData.pages.map((page: any) => ({
                  ...page,
                  posts: page.posts.map((post: any) => 
                    post.id === variables.post_id
                      ? {
                          ...post,
                          comments: (post.comments || []).filter(
                            (comment: any) => comment.id !== context.optimisticComment.id
                          )
                        }
                      : post
                  )
                }))
              };
            }
            
            // Handle regular query structure
            if (Array.isArray(oldData)) {
              return oldData.map((post: any) => 
                post.id === variables.post_id
                  ? {
                      ...post,
                      comments: (post.comments || []).filter(
                        (comment: any) => comment.id !== context.optimisticComment.id
                      )
                    }
                  : post
              );
            }
            
            return oldData;
          }
        );
      }
    },
    ...options,
  });
};

export const useDeleteComment = (
  options?: UseMutationOptions<any, Error, string>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteComment,
    onSuccess: () => {
      // Invalidate queries to ensure consistency (feed + reels)
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      queryClient.invalidateQueries({ queryKey: ['reels-posts'] });
    },
    onError: () => {
      // Refetch on error to restore correct state
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      queryClient.invalidateQueries({ queryKey: ['reels-posts'] });
    },
    ...options,
  });
};

// Likes API Functions
const fetchLikes = async (postId: string) => {
  if (!postId || postId === 'null' || postId === 'undefined') {
    throw new Error('Invalid postId provided to fetchLikes');
  }
  const response = await apiClient.get(`/likes/${postId}`);
  // Backend now returns an array of likes with user information
  const likes = Array.isArray(response.data) ? response.data : [];
  return likes;
};

const toggleLike = async (postId: string, userId: number) => {
  if (!postId || postId === 'null' || postId === 'undefined') {
    throw new Error('Invalid postId provided to toggleLike');
  }
  if (!userId || userId <= 0) {
    throw new Error('Invalid userId provided to toggleLike');
  }
  const bodyData = { 
    postId: Number(postId),
    userId: Number(userId)
  };
  const response = await apiClient.post('/likes', bodyData);
  return response.data;
};

// Likes Hooks
export const useLikes = (
  postId: string,
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.likes(postId),
    queryFn: () => fetchLikes(postId),
    enabled: !!postId && postId !== 'null' && postId !== 'undefined',
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
  });
};

export const useToggleLike = (
  options?: UseMutationOptions<any, Error, { postId: string; userId: number }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, userId }) => toggleLike(postId, userId),
    onSuccess: (_, { postId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.likes(postId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
      queryClient.invalidateQueries({ queryKey: ['reels-posts'] });
    },
    ...options,
  });
};

/** Optimistic like state override: { liked, count } or null to use server state. */
type OptimisticLikeState = { liked: boolean; count: number } | null;

/** Options for useOptimisticLike: optional onError callback when the API fails (after rollback). */
export type UseOptimisticLikeOptions = {
  onError?: (error: Error) => void;
};

/**
 * Updates all post-related query caches so a given post's like state is correct.
 * Call this on successful like API response so that when we clear optimistic state,
 * the UI reads the updated cache and doesn't flicker.
 */
function updatePostLikeInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  postId: string,
  userId: number,
  nextLiked: boolean
) {
  const postIdStr = String(postId);

  const updatePost = (p: any) => {
    if (!p || String(p.id) !== postIdStr) return p;
    const currentLikes = Array.isArray(p.likes) ? p.likes : [];
    const newLikes = nextLiked
      ? [...currentLikes, { userId, user_id: userId, user: { id: userId } }]
      : currentLikes.filter(
          (l: any) => Number(l.userId ?? l.user_id ?? l.user?.id) !== Number(userId)
        );
    return { ...p, isLikedByCurrentUser: nextLiked, likes: newLikes };
  };

  const updater = (data: any) => {
    if (!data) return data;
    if (data.id != null && String(data.id) === postIdStr) return updatePost(data);
    if (Array.isArray(data.posts)) {
      return { ...data, posts: data.posts.map((p: any) => updatePost(p)) };
    }
    if (data.pages && Array.isArray(data.pages)) {
      return {
        ...data,
        pages: data.pages.map((page: any) =>
          Array.isArray(page.posts)
            ? { ...page, posts: page.posts.map((p: any) => updatePost(p)) }
            : page
        ),
      };
    }
    return data;
  };

  queryClient.setQueriesData(
    { predicate: (query) => query.queryKey[0] === 'posts' || query.queryKey[0] === 'reels-posts' },
    updater
  );
}

/**
 * Hook for optimistic like toggle (Instagram-style):
 * 1. On click: heart and count update immediately (optimistic state).
 * 2. API request runs in the background (non-blocking).
 * 3. On success: cache is updated, optimistic state cleared so UI stays correct.
 * 4. On failure: optimistic state cleared (rollback), optional onError called.
 */
export const useOptimisticLike = (
  postId: string,
  userId: number | undefined,
  likesCount: number,
  hasLikes: boolean,
  options?: UseOptimisticLikeOptions
) => {
  const queryClient = useQueryClient();
  const [optimisticState, setOptimisticState] = useState<OptimisticLikeState>(null);
  const requestLockRef = useRef(false);
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const toggleLikeMutation = useToggleLike();

  const displayLiked = optimisticState?.liked ?? hasLikes;
  const displayCount = optimisticState?.count ?? likesCount;

  const likePost = useCallback(() => {
    if (!userId || !postId || postId === 'null' || postId === 'undefined') return;
    if (requestLockRef.current) return;
    requestLockRef.current = true;

    const nextLiked = !displayLiked;
    const nextCount = Math.max(0, displayCount + (nextLiked ? 1 : -1));
    setOptimisticState({ liked: nextLiked, count: nextCount });

    toggleLikeMutation.mutate(
      { postId, userId },
      {
        onSuccess: (_, variables) => {
          updatePostLikeInCache(queryClient, variables.postId, variables.userId, nextLiked);
          setOptimisticState(null);
        },
        onError: (error) => {
          setOptimisticState(null);
          onErrorRef.current?.(error);
        },
        onSettled: () => {
          requestLockRef.current = false;
        },
      }
    );
  }, [postId, userId, displayLiked, displayCount, queryClient, toggleLikeMutation]);

  return {
    displayLiked,
    displayCount,
    likePost,
    isPending: toggleLikeMutation.isPending,
  };
};

// Stories API Functions
const fetchStories = async () => {
  const response = await apiClient.get('/stories');
  return response.data;
};

export type CreateStoryPayload = { file: File; postId?: string };

const createStory = async (payload: File | CreateStoryPayload) => {
  const formData = new FormData();
  const file = typeof payload === 'object' && payload && 'file' in payload ? payload.file : payload;
  const postId = typeof payload === 'object' && payload && 'postId' in payload ? payload.postId : undefined;
  formData.append('file', file);
  if (postId) formData.append('post_id', postId);

  const response = await apiClient.post('/stories', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes timeout for large files
  });
  return response.data;
};

const deleteStory = async (storyId: string) => {
  const response = await apiClient.delete(`/stories/${storyId}`);
  return response.data;
};

const markStoryViewed = async (storyId: string) => {
  const response = await apiClient.post(`/stories/${storyId}/seen`);
  return response.data;
};

// Stories Hooks
export const useStories = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.stories,
    queryFn: fetchStories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCreateStory = (
  options?: UseMutationOptions<any, Error, Parameters<typeof createStory>[0]>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createStory,
    onSuccess: () => {
      // Invalidate stories to refetch updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.stories });
      // Immediately refetch to show new content
      queryClient.refetchQueries({ queryKey: queryKeys.stories });
      
      // Notify service worker to refresh cache if app is installed as PWA
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_UPDATE',
          action: 'invalidate',
        });
      }
    },
    ...options,
  });
};

export const useDeleteStory = (
  options?: UseMutationOptions<any, Error, string>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteStory,
    onMutate: async (storyId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.stories });
      
      // Optimistically remove the story from stories query
      queryClient.setQueriesData(
        { queryKey: queryKeys.stories },
        (oldData: any) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          return oldData.filter((story: any) => story.id !== storyId);
        }
      );
    },
    onSuccess: () => {
      // Invalidate stories queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.stories });
    },
    onError: () => {
      // Refetch on error to restore correct state
      queryClient.invalidateQueries({ queryKey: queryKeys.stories });
    },
    ...options,
  });
};

export const useMarkStoryViewed = (
  options?: UseMutationOptions<any, Error, string>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markStoryViewed,
    onSuccess: (_, storyId) => {
      // Optimistically set seen=true for this story in cache
      queryClient.setQueriesData(
        { queryKey: queryKeys.stories },
        (oldData: Story[] | undefined) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          return oldData.map((s) =>
            String(s.id) === String(storyId) ? { ...s, seen: true } : s
          );
        }
      );
    },
    ...options,
  });
};

// Tags API Functions
const fetchAllTags = async () => {
  const response = await apiClient.get('/tags');
  return response.data;
};

const fetchPopularTags = async (limit: number = 10) => {
  const response = await apiClient.get(`/tags/popular?limit=${limit}`);
  return response.data;
};

const fetchSearchTags = async (q: string, limit: number = 20) => {
  const response = await apiClient.get(
    `/tags/search?q=${encodeURIComponent(q.trim())}&limit=${limit}`
  );
  return response.data;
};

const fetchTagsByPost = async (postId: string) => {
  const response = await apiClient.get(`/tags/${postId}`);
  return response.data;
};

const addTag = async (tagData: {
  post_id: number;
  name: string;
}) => {
  const bodyData = {
    PostID: tagData.post_id,
    Name: tagData.name,
  };
  const response = await apiClient.post('/tags', bodyData);
  return response.data;
};

/** Create a new tag by name. Backend returns 400 if tag already exists. */
const createTag = async (name: string) => {
  const response = await apiClient.post('/tags', { name: name.trim().toLowerCase() });
  return response.data;
};

// Tags Hooks
export const useAllTags = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.tags,
    queryFn: fetchAllTags,
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error) => {
      // Don't retry on 500 errors for tags - just return empty array
      if (error.message.includes('500') || error.message.includes('سرور')) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

export const usePopularTags = (
  limit: number = 10,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.popularTags, limit],
    queryFn: () => fetchPopularTags(limit),
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error) => {
      // Don't retry on 500 errors for tags - just return empty array
      if (error.message.includes('500') || error.message.includes('سرور')) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

export const useSearchTags = (
  q: string,
  limit: number = 20,
  options?: Omit<UseQueryOptions<Tag[], Error>, 'queryKey' | 'queryFn'>
) => {
  const query = q.trim().toLowerCase();
  return useQuery({
    queryKey: [...queryKeys.tagsSearch(query), limit],
    queryFn: () => fetchSearchTags(query, limit),
    enabled: query.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error.message.includes('500') || error.message.includes('سرور')) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

export const useTagsByPost = (
  postId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.tagsByPost(postId),
    queryFn: () => fetchTagsByPost(postId),
    enabled: !!postId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

export const useAddTag = (
  options?: UseMutationOptions<any, Error, Parameters<typeof addTag>[0]>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: addTag,
    onSuccess: (_, variables) => {
      // Invalidate tags for the specific post
      queryClient.invalidateQueries({ queryKey: queryKeys.tagsByPost(variables.post_id.toString()) });
      // Invalidate all tags
      queryClient.invalidateQueries({ queryKey: queryKeys.tags });
      // Invalidate popular tags
      queryClient.invalidateQueries({ queryKey: queryKeys.popularTags });
      // Invalidate posts to update tag data
      queryClient.invalidateQueries({ queryKey: queryKeys.posts });
    },
    ...options,
  });
};

export const useCreateTag = (
  options?: UseMutationOptions<any, Error, string>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags });
      queryClient.invalidateQueries({ queryKey: queryKeys.popularTags });
    },
    ...options,
  });
};

// Users API Functions
const fetchUser = async (userId: string) => {
  const response = await apiClient.get(`/users/${userId}`);
  return response.data;
};

const fetchUserPostCount = async (userId: string) => {
  const response = await apiClient.get(`/users/${userId}/posts/count`);
  return response.data;
};

type UsersFilter = 'all' | 'verified' | 'new';

const fetchAllUsers = async (
  page: number = 1,
  limit: number = 20,
  filter: UsersFilter = 'all',
  search: string = '',
  sort?: string
) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());

  if (filter === 'verified' || filter === 'new') {
    params.append('filter', filter);
  }

  if (search.trim()) {
    params.append('search', search.trim());
  }

  if (sort === 'coins') {
    params.append('sort', 'coins');
  }

  const response = await apiClient.get(`/users?${params.toString()}`);
  return response.data;
};

// Follow API Functions
const followUser = async (userId: string) => {
  const response = await apiClient.post(`/users/${userId}/follow`);
  return response.data;
};

const unfollowUser = async (userId: string) => {
  const response = await apiClient.post(`/users/${userId}/unfollow`);
  return response.data;
};

const getFollowStatus = async (userId: string) => {
  const response = await apiClient.get(`/users/${userId}/follow-status`);
  return response.data;
};

const getFollowers = async (userId: string, page: number = 1, limit: number = 20) => {
  const response = await apiClient.get(`/users/${userId}/followers?page=${page}&limit=${limit}`);
  return response.data;
};

const getFollowing = async (userId: string, page: number = 1, limit: number = 20) => {
  const response = await apiClient.get(`/users/${userId}/following?page=${page}&limit=${limit}`);
  return response.data;
};

const getFollowStats = async (userId: string) => {
  const response = await apiClient.get(`/users/${userId}/follow-stats`);
  return response.data;
};

const updateUser = async (userId: string, userData: any) => {
  const response = await apiClient.put(`/users/${userId}`, userData);
  return response.data;
};

// Users Hooks
export const useUser = (
  userId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.user(userId),
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

export const useUserPostCount = (
  userId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.userPostCount(userId),
    queryFn: () => fetchUserPostCount(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - shorter than user data since post count changes more frequently
    ...options,
  });
};

export interface CoinTransaction {
  id: number;
  user_id: number;
  amount: number;
  type: string;
  ref_id?: number;
  created_at: string;
}

const fetchCoinHistory = async (page: number = 1, limit: number = 10): Promise<CoinTransaction[]> => {
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('limit', String(limit));
  const response = await apiClient.get(`/user/coins/history?${params.toString()}`);
  return response.data;
};

export const useCoinHistory = (
  page: number = 1,
  limit: number = 10,
  options?: Omit<UseQueryOptions<CoinTransaction[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.coinHistory, page, limit],
    queryFn: () => fetchCoinHistory(page, limit),
    staleTime: 60 * 1000, // 1 minute
    ...options,
  });
};

export const useAllUsers = (
  page: number = 1,
  limit: number = 20,
  filter: UsersFilter = 'all',
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.allUsers, page, limit, filter],
    queryFn: () => fetchAllUsers(page, limit, filter),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/** Users sorted by coins DESC; response items include `coins`. */
export const useCoinLeaderboard = (
  page: number = 1,
  limit: number = 50,
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.coinLeaderboard, page, limit],
    queryFn: () => fetchAllUsers(page, limit, 'all', '', 'coins'),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useInfiniteAllUsers = (
  limit: number = 15,
  filter: UsersFilter = 'all',
  search: string = '',
  options?: Omit<UseInfiniteQueryOptions<any, Error, any, any, number>, 'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'>
) => {
  return useInfiniteQuery({
    queryKey: ['infinite', filter, search.trim()],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      fetchAllUsers(pageParam, limit, filter, search),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer users than the limit, there are no more pages
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < limit) {
        return undefined;
      }
      return allPages.length + 1;
    },
    
    staleTime: 2 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useUpdateUser = (
  options?: UseMutationOptions<any, Error, { userId: string; userData: any }>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, userData }) => updateUser(userId, userData),
    onSuccess: (_, variables) => {
      // Invalidate user data
      queryClient.invalidateQueries({ queryKey: queryKeys.user(variables.userId) });
    },
    ...options,
  });
};

// Get current user profile (authenticated). Use this to load full profile including description and admin.
export const fetchCurrentUserProfile = async (): Promise<{
  id: number;
  username: string;
  name: string;
  image: string;
  description: string;
  verified?: boolean;
  followers_count?: number;
  following_count?: number;
  admin?: boolean;
}> => {
  const response = await apiClient.get('/user/profile');
  return response.data;
};

export const useCurrentUserProfile = (
  enabled: boolean,
  options?: Omit<UseQueryOptions<Awaited<ReturnType<typeof fetchCurrentUserProfile>>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: fetchCurrentUserProfile,
    enabled,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

// Change Password API Function
export const changePassword = async (passwordData: {
  current_password: string;
  new_password: string;
}) => {
  const response = await apiClient.put('/user/change-password', passwordData);
  return response.data;
};

// Verification Request API Functions
const createVerificationRequest = async (file: File) => {
  const formData = new FormData();
  formData.append('passport', file);

  const response = await apiClient.post('/user/verification-request', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes timeout for large files
  });
  return response.data;
};

const getVerificationRequest = async () => {
  const response = await apiClient.get('/user/verification-request');
  return response.data;
};

// Verification Request Hooks
export const useVerificationRequest = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.verificationRequest,
    queryFn: getVerificationRequest,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no request exists yet)
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

export const useCreateVerificationRequest = (
  options?: UseMutationOptions<any, Error, File>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createVerificationRequest,
    onSuccess: () => {
      // Invalidate verification request query to refetch updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.verificationRequest });
      // Invalidate user data to update verified status
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options,
  });
};

// Auth API Functions
const signup = async (userData: {
  username: string;
  password: string;
  phone_number: string;
  description?: string;
  image?: string;
}) => {
  const response = await apiClient.post('/signup', userData);
  return response.data;
};

const verifyPhone = async (verificationData: {
  phone_number: string;
  code: string;
}) => {
  const response = await apiClient.post('/verify-phone', verificationData);
  return response.data;
};

const login = async (credentials: {
  username: string;
  password: string;
}) => {
  const response = await apiClient.post('/login', credentials);
  return response.data;
};

// Auth Hooks
export const useSignup = (
  options?: UseMutationOptions<any, Error, Parameters<typeof signup>[0]>
) => {
  return useMutation({
    mutationFn: signup,
    ...options,
  });
};

export const useVerifyPhone = (
  options?: UseMutationOptions<any, Error, Parameters<typeof verifyPhone>[0]>
) => {
  return useMutation({
    mutationFn: verifyPhone,
    ...options,
  });
};

export const useLogin = (
  options?: UseMutationOptions<any, Error, Parameters<typeof login>[0]>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: login,
    onSuccess: () => {
      // Auth state is set by login(); do not store token/user in JS cookies (backend sets HttpOnly cookie).
      queryClient.clear();
    },
    ...options,
  });
};

// Generic API client for simple requests (using the configured apiClient)
export const api = {
  get: async (endpoint: string) => {
    const response = await apiClient.get(endpoint);
    return response.data;
  },

  post: async (endpoint: string, data: any) => {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  },

  put: async (endpoint: string, data: any) => {
    const response = await apiClient.put(endpoint, data);
    return response.data;
  },

  delete: async (endpoint: string) => {
    const response = await apiClient.delete(endpoint);
    return response.data;
  },
};

// Token management utilities (using cookies)
export const tokenUtils = {
  /**
   * Get the current authentication token from cookies
   */
  getToken: () => {
    const rawToken = getAuthToken();
    return validateAndCleanToken(rawToken);
  },
  
  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated: () => {
    const token = tokenUtils.getToken();
    return !!token && token.length > 0;
  },
  
  /**
   * Validate current token and clean invalid ones
   */
  validateToken: () => {
    const rawToken = getAuthToken();
    const validToken = validateAndCleanToken(rawToken);
    
    if (rawToken && !validToken) {
      // Token exists but is invalid, remove it
      removeAuthToken();
      removeAuthUser();
      return false;
    }
    
    return !!validToken;
  },
  
  /**
   * Clear authentication data and invalidate all cached queries
   */
  logout: (queryClient: ReturnType<typeof useQueryClient>) => {
    // Clear cookies
    removeAuthToken();
    removeAuthUser();
    
    // Clear all cached data
    queryClient.clear();
  }
};

// Logout hook for easy use in components
export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return () => {
    tokenUtils.logout(queryClient);
  };
};

// Hook to validate token on app startup
export const useTokenValidation = () => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['token-validation'],
    queryFn: async () => {
      const isValid = tokenUtils.validateToken();
      if (!isValid) {
        // Clear all cached data if token is invalid
        queryClient.clear();
      }
      return isValid;
    },
    staleTime: Infinity, // Only run once per session
    retry: false,
  });
};

// Poll Hooks
export const useVoteOnPostPoll = (
  options?: UseMutationOptions<any, Error, { pollId: number; optionId: number }>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ pollId, optionId }) => voteOnPostPoll(pollId, optionId),
    onSuccess: () => {
      // Invalidate posts queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    ...options,
  });
};

export const usePostPollResults = (
  pollId: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['post-poll-results', pollId],
    queryFn: () => getPostPollResults(pollId),
    enabled: !!pollId,
    ...options,
  });
};

// Hash-based Poll Hooks
export const useVoteOnPostPollByHash = (
  options?: UseMutationOptions<any, Error, { hashCode: string; optionId: number }>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ hashCode, optionId }) => voteOnPostPollByHash(hashCode, optionId),
    onSuccess: () => {
      // Invalidate posts queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    ...options,
  });
};

export const usePostPollResultsByHash = (
  hashCode: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['post-poll-results-hash', hashCode],
    queryFn: () => getPostPollResultsByHash(hashCode),
    enabled: !!hashCode,
    ...options,
  });
};

// Follow Hooks
export const useFollowUser = (
  options?: UseMutationOptions<any, Error, string, { previousStatus: unknown }>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: followUser,
    onMutate: async (userId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['follow-status', userId] });
      
      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData(['follow-status', userId]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['follow-status', userId], { following: true });
      
      // Return a context object with the snapshotted value
      return { previousStatus };
    },
    onError: (err, userId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousStatus) {
        queryClient.setQueryData(['follow-status', userId], context.previousStatus);
      }
    },
    onSuccess: (_, userId) => {
      // Invalidate user data to update follow stats
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
      // Invalidate follow status to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['follow-status', userId] });
      // Invalidate followers list
      queryClient.invalidateQueries({ queryKey: ['followers', userId] });
      // Invalidate all users list to update follow buttons (including paginated queries)
      queryClient.invalidateQueries({ queryKey: queryKeys.allUsers, exact: false });
      // Invalidate infinite users list (explorer) so is_following refetches from API
      queryClient.invalidateQueries({ queryKey: ['infinite'], exact: false });
    },
    onSettled: (_, __, userId) => {
      queryClient.invalidateQueries({ queryKey: ['follow-status', userId] });
    },
    ...options,
  });
};

export const useUnfollowUser = (
  options?: UseMutationOptions<any, Error, string, { previousStatus: unknown }>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: unfollowUser,
    onMutate: async (userId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['follow-status', userId] });
      
      // Snapshot the previous value
      const previousStatus = queryClient.getQueryData(['follow-status', userId]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['follow-status', userId], { following: false });
      
      // Return a context object with the snapshotted value
      return { previousStatus };
    },
    onError: (err, userId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousStatus) {
        queryClient.setQueryData(['follow-status', userId], context.previousStatus);
      }
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
      queryClient.invalidateQueries({ queryKey: ['follow-status', userId] });
      queryClient.invalidateQueries({ queryKey: ['followers', userId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.allUsers, exact: false });
      queryClient.invalidateQueries({ queryKey: ['infinite'], exact: false });
    },
    onSettled: (_, __, userId) => {
      queryClient.invalidateQueries({ queryKey: ['follow-status', userId] });
    },
    ...options,
  });
};

export const useFollowStatus = (
  userId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['follow-status', userId],
    queryFn: () => getFollowStatus(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useFollowers = (
  userId: string,
  page: number = 1,
  limit: number = 20,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['followers', userId, page, limit],
    queryFn: () => getFollowers(userId, page, limit),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useFollowing = (
  userId: string,
  page: number = 1,
  limit: number = 20,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['following', userId, page, limit],
    queryFn: () => getFollowing(userId, page, limit),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useFollowStats = (
  userId: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['follow-stats', userId],
    queryFn: () => getFollowStats(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

// Notifications API Functions
const fetchNotifications = async (limit: number = 20, offset: number = 0, unreadOnly: boolean = false) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (offset) params.append('offset', offset.toString());
  if (unreadOnly) params.append('unread_only', 'true');
  
  const response = await apiClient.get(`/notifications?${params.toString()}`);
  return response.data;
};

const getUnreadNotificationCount = async () => {
  const response = await apiClient.get('/notifications/unread-count');
  return response.data;
};

const markNotificationAsRead = async (notificationId: string) => {
  const response = await apiClient.put(`/notifications/${notificationId}/read`);
  return response.data;
};

const markAllNotificationsAsRead = async () => {
  const response = await apiClient.put('/notifications/read-all');
  return response.data;
};

// Notifications Hooks
export const useNotifications = (
  limit: number = 20,
  offset: number = 0,
  unreadOnly: boolean = false,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.notifications, limit, offset, unreadOnly],
    queryFn: () => fetchNotifications(limit, offset, unreadOnly),
    staleTime: 30 * 1000, // 30 seconds - notifications should be fresh
    refetchInterval: 60 * 1000, // Poll every minute for new notifications
    ...options,
  });
};

export const useInfiniteNotifications = (
  limit: number = 8,
  unreadOnly: boolean = false,
  options?: Omit<UseInfiniteQueryOptions<any, Error, any, any, any>, 'queryKey' | 'queryFn'>
) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications, 'infinite', limit, unreadOnly],
    queryFn: ({ pageParam = 0 }) => fetchNotifications(limit, pageParam, unreadOnly),
    getNextPageParam: (lastPage: any[] | { items?: any[] }, allPages) => {
      const items = Array.isArray(lastPage) ? lastPage : lastPage?.items ?? [];
      if (items.length < limit) return undefined;
      return allPages.length * limit;
    },
    initialPageParam: 0,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    ...options,
  });
};

export const useUnreadNotificationCount = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.unreadNotificationCount,
    queryFn: getUnreadNotificationCount,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every minute for new notifications
    ...options,
  });
};

export const useMarkNotificationAsRead = (
  options?: UseMutationOptions<any, Error, string, any>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      // Invalidate notifications and unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
    },
    ...options,
  });
};

export const useMarkAllNotificationsAsRead = (
  options?: UseMutationOptions<any, Error, void, any>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      // Invalidate notifications and unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      queryClient.invalidateQueries({ queryKey: queryKeys.unreadNotificationCount });
    },
    ...options,
  });
};

// Direct message API functions
const fetchConversations = async (limit: number = 20, beforeId?: number) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (beforeId != null) params.append('before_id', String(beforeId));
  const response = await apiClient.get(`/conversations?${params.toString()}`);
  return response.data;
};

const fetchConversation = async (conversationId: number) => {
  const response = await apiClient.get(`/conversations/${conversationId}`);
  return response.data;
};

const getOrCreateConversation = async (otherUserId: number) => {
  const response = await apiClient.get(`/conversations/find-or-create?user_id=${otherUserId}`);
  return response.data;
};

const fetchMessages = async (conversationId: number, limit: number = 30, beforeId?: number) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (beforeId != null) params.append('before_id', String(beforeId));
  const response = await apiClient.get(`/conversations/${conversationId}/messages?${params.toString()}`);
  return response.data;
};

const sendMessage = async (
  conversationId: number,
  body: string,
  attachmentUrl?: string,
  storyId?: number | null
) => {
  const response = await apiClient.post(`/conversations/${conversationId}/messages`, {
    body,
    attachmentUrl: attachmentUrl || undefined,
    storyId: storyId ?? undefined,
  });
  return response.data;
};

const markConversationRead = async (conversationId: number) => {
  const response = await apiClient.put(`/conversations/${conversationId}/read`);
  return response.data;
};

const getDMUnreadCount = async () => {
  const response = await apiClient.get('/conversations/unread-count');
  return response.data;
};

// DM Hooks
export const useConversations = (
  limit: number = 20,
  beforeId?: number,
  options?: Omit<UseQueryOptions<DMConversation[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.dmConversations, limit, beforeId ?? 'initial'],
    queryFn: () => fetchConversations(limit, beforeId),
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useInfiniteConversations = (
  limit: number = 20,
  userId?: number | null,
  options?: object
) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.dmConversations, 'infinite', limit, userId ?? 'anon'],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      fetchConversations(limit, pageParam ?? undefined),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage: DMConversation[]) => {
      if (!lastPage?.length || lastPage.length < limit) return undefined;
      return lastPage[lastPage.length - 1]?.id as number | undefined;
    },
    staleTime: 30 * 1000,
    enabled: userId != null,
    ...options,
  });
};

export const useConversation = (
  conversationId: number | undefined,
  options?: Omit<UseQueryOptions<DMConversation, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.dmConversation(String(conversationId ?? '')),
    queryFn: () => fetchConversation(conversationId!),
    enabled: !!conversationId,
    staleTime: 60 * 1000,
    ...options,
  });
};

type GetOrCreateConversationResponse = { conversation: DMConversation; created: boolean };

export const useGetOrCreateConversation = (
  _otherUserId: number | undefined,
  options?: UseMutationOptions<GetOrCreateConversationResponse, Error, number>
) => {
  const queryClient = useQueryClient();
  return useMutation<GetOrCreateConversationResponse, Error, number>({
    mutationFn: (uid: number) => getOrCreateConversation(uid),
    onSuccess: (data) => {
      if (data?.conversation?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dmConversations });
        queryClient.setQueryData(queryKeys.dmConversation(String(data.conversation.id)), data.conversation);
      }
    },
    ...options,
  });
};

export const useMessages = (
  conversationId: number | undefined,
  limit: number = 30,
  beforeId?: number,
  options?: Omit<UseQueryOptions<DMMessage[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.dmMessages(String(conversationId ?? '')), limit, beforeId ?? 'initial'],
    queryFn: () => fetchMessages(conversationId!, limit, beforeId),
    enabled: !!conversationId,
    staleTime: 15 * 1000,
    ...options,
  });
};

export const useInfiniteMessages = (conversationId: number | undefined, limit: number = 30, options?: object) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.dmMessages(String(conversationId ?? '')), 'infinite', limit],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      fetchMessages(conversationId!, limit, pageParam ?? undefined),
    enabled: !!conversationId,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage: DMMessage[]) => {
      if (!lastPage?.length || lastPage.length < limit) return undefined;
      return lastPage[lastPage.length - 1]?.id as number | undefined;
    },
    staleTime: 15 * 1000,
    ...options,
  });
};

export const useSendMessage = (
  conversationId: number | undefined,
  options?: UseMutationOptions<DMMessage, Error, { body: string; attachmentUrl?: string }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, attachmentUrl }) => sendMessage(conversationId!, body, attachmentUrl),
    onSuccess: (data) => {
      if (data?.conversationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dmMessages(String(data.conversationId)) });
        queryClient.invalidateQueries({ queryKey: queryKeys.dmConversations });
        queryClient.invalidateQueries({ queryKey: queryKeys.dmUnreadCount });
      }
    },
    ...options,
  });
};

export const useMarkConversationRead = (
  options?: UseMutationOptions<void, Error, number>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (convId: number) => markConversationRead(convId),
    onSuccess: (_, convId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dmMessages(String(convId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dmConversations });
      queryClient.invalidateQueries({ queryKey: queryKeys.dmUnreadCount });
    },
    ...options,
  });
};

export const useDMUnreadCount = (
  userId?: number | null,
  options?: Omit<UseQueryOptions<{ count: number }, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.dmUnreadCount, userId ?? 'anon'],
    queryFn: getDMUnreadCount,
    staleTime: 30 * 1000,
    enabled: userId != null,
    ...options,
  });
};

// Prediction games types and API
export interface GamePredictionOption {
  id: number;
  gameId: number;
  type: string;
  label: string;
  orderIndex: number;
  correctValue?: string | null;
}

export interface PredictionGame {
  id: number;
  matchId: number;
  title: string;
  description: string;
  coinCost: number;
  status: 'draft' | 'open' | 'closed' | 'resolved' | 'canceled';
  minParticipants?: number | null;
  createdByUserId: number;
  createdAt: string;
  options: GamePredictionOption[];
  match?: {
    id: number;
    host?: { name: string; logo?: string };
    guest?: { name: string; logo?: string };
    date?: string;
    time?: string;
  };
  participantCount?: number;
  totalPrizePoolCoins?: number;
  winnerPayoutCoins?: number;
  myParticipant?: { id: number; joinedAt: string; correctCount?: number; prizeCoins?: number };
  myPredictions?: Array<{ optionId: number; value: string }>;
}

const fetchGames = async (status?: string) => {
  const url = status ? `/games?status=${encodeURIComponent(status)}` : '/games';
  const response = await apiClient.get(url);
  return response.data;
};

const fetchGame = async (id: string) => {
  const response = await apiClient.get(`/games/${id}`);
  return response.data;
};

const fetchGameConstants = async () => {
  const response = await apiClient.get('/games/constants');
  return response.data;
};

const joinGame = async (gameId: number) => {
  const response = await apiClient.post(`/games/${gameId}/join`, {});
  return response.data;
};

const submitGamePredictions = async (gameId: number, predictions: Array<{ optionId: number; value: string }>) => {
  const response = await apiClient.post(`/games/${gameId}/predictions`, { predictions });
  return response.data;
};

export interface CreateGameInput {
  matchId: number;
  title: string;
  description: string;
  coinCost: number;
  minParticipants?: number | null;
  /** draft = not on public open list; open = visible and joinable */
  status?: 'draft' | 'open';
  options: Array<{ type: string; label: string; order: number }>;
}

const createGame = async (data: CreateGameInput) => {
  const response = await apiClient.post("/games", data);
  return response.data;
};

export const useCreateGame = (options?: UseMutationOptions<any, Error, CreateGameInput>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createGame,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
    },
    ...options,
  });
};

const deleteGame = async (gameId: number) => {
  const response = await apiClient.delete(`/games/${gameId}`);
  return response.data;
};

export interface AdminGameParticipant {
  id: number;
  userId: number;
  joinedAt: string;
  correctCount?: number | null;
  prizeCoins?: number | null;
  predictions: Array<{ optionId: number; value: string; correct: boolean }>;
  previewCorrect: number;
  previewWinner?: boolean;
  user?: { id: number; name: string; username: string; image?: string; verified?: boolean };
}

export interface AdminGameResponse extends PredictionGame {
  participants: AdminGameParticipant[];
  preview: {
    allCorrectValuesSet: boolean;
    maxCorrect: number;
    winnersCount: number;
    totalPrizePoolCoins: number;
    coinsPerWinner: number;
  };
}

const fetchAdminGame = async (id: string): Promise<AdminGameResponse> => {
  const response = await apiClient.get(`/admin/games/${id}`);
  return response.data;
};

export const useAdminGame = (
  id: string | undefined,
  options?: Omit<UseQueryOptions<AdminGameResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.game(id ?? ''), 'admin'],
    queryFn: () => fetchAdminGame(id!),
    enabled: !!id,
    staleTime: 0,
    ...options,
  });
};

const setGameCorrectValues = async (
  gameId: number,
  correctValues: Array<{ optionId: number; value: string }>
) => {
  const response = await apiClient.put(`/games/${gameId}/correct-values`, { correctValues });
  return response.data;
};

export const useSetGameCorrectValues = (
  options?: UseMutationOptions<
    { message: string },
    Error,
    { gameId: number; correctValues: Array<{ optionId: number; value: string }> }
  >
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId, correctValues }) => setGameCorrectValues(gameId, correctValues),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.game(String(variables.gameId)), 'admin'] });
    },
    ...options,
  });
};

const resolveGame = async (gameId: number) => {
  const response = await apiClient.post(`/games/${gameId}/resolve`, {});
  return response.data;
};

export interface ResolveGameResult {
  message: string;
  totalPrizePoolCoins?: number;
  winnersCount?: number;
  coinsPerWinner?: number;
  winnerUserIds?: number[];
}

export const useResolveGame = (
  options?: UseMutationOptions<ResolveGameResult, Error, { gameId: number }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId }) => resolveGame(gameId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.game(String(variables.gameId)), 'admin'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
      queryClient.invalidateQueries({ queryKey: queryKeys.coinHistory });
    },
    ...options,
  });
};

const updateGameStatus = async (gameId: number, status: 'open' | 'closed' | 'canceled') => {
  const response = await apiClient.put(`/games/${gameId}/status?status=${encodeURIComponent(status)}`);
  return response.data;
};

export const useUpdateGameStatus = (
  options?: UseMutationOptions<any, Error, { gameId: number; status: 'open' | 'closed' | 'canceled' }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId, status }) => updateGameStatus(gameId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.game(String(variables.gameId)), 'admin'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
    },
    ...options,
  });
};

export const useDeleteGame = (
  options?: UseMutationOptions<{ message: string; id: number; refundedUsers: number; refundedCoinsEach: number }, Error, { gameId: number }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId }) => deleteGame(gameId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
      queryClient.removeQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
    },
    ...options,
  });
};

export const useGames = (
  status?: string,
  options?: Omit<UseQueryOptions<PredictionGame[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...queryKeys.games, status ?? 'all'],
    queryFn: () => fetchGames(status),
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useGame = (
  id: string | undefined,
  options?: Omit<UseQueryOptions<PredictionGame, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.game(id ?? ''),
    queryFn: () => fetchGame(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useGameConstants = (
  options?: Omit<UseQueryOptions<{ coinPriceToman: number }, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.gameConstants,
    queryFn: fetchGameConstants,
    staleTime: 60 * 60 * 1000,
    ...options,
  });
};

export const useJoinGame = (options?: UseMutationOptions<any, Error, { gameId: number }>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId }) => joinGame(gameId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
      queryClient.invalidateQueries({ queryKey: queryKeys.games });
      queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
      queryClient.invalidateQueries({ queryKey: queryKeys.coinHistory });
    },
    ...options,
  });
};

export const useSubmitGamePredictions = (
  options?: UseMutationOptions<any, Error, { gameId: number; predictions: Array<{ optionId: number; value: string }> }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameId, predictions }) => submitGamePredictions(gameId, predictions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.game(String(variables.gameId)) });
    },
    ...options,
  });
};

// Export upload functions for direct use
export { uploadMultipleFiles, uploadSingleFile, voteOnPostPoll, getPostPollResults, voteOnPostPollByHash, getPostPollResultsByHash, fetchPosts, fetchReels };

// Export notification functions for direct use
export { getUnreadNotificationCount };

// Story reply: get-or-create conversation then send message (for story DM reply)
export const sendStoryReply = async (
  otherUserId: number,
  body: string,
  storyId?: number | null
) => {
  const { conversation } = await getOrCreateConversation(otherUserId);
  return sendMessage(conversation.id, body, undefined, storyId);
};

export const useSendStoryReply = (
  options?: UseMutationOptions<DMMessage, Error, { otherUserId: number; body: string; storyId?: number | null }>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ otherUserId, body, storyId }) => sendStoryReply(otherUserId, body, storyId),
    onSuccess: (data) => {
      if (data?.conversationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dmMessages(String(data.conversationId)) });
        queryClient.invalidateQueries({ queryKey: queryKeys.dmConversations });
        queryClient.invalidateQueries({ queryKey: queryKeys.dmUnreadCount });
      }
    },
    ...options,
  });
};

// Export DM functions for direct use
export { getDMUnreadCount, fetchMessages, fetchConversations };

// Export the configured axios client for direct use if needed
export { apiClient,getApiUrl };
